import React, { useState, useEffect } from 'react';
import { ChevronRight, FileText, Shield, Globe, Users, MessageSquare, AlertTriangle, Phone, Mail, MapPin, Clock, ChevronUp, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfUse = () => {
  const [activeSection, setActiveSection] = useState('main');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setReadingProgress(scrollPercent);
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const mainSections = [
    { id: 'acceptance', title: 'Acceptance of Terms', icon: Shield },
    { id: 'modification', title: 'Modification of Terms', icon: FileText },
    { id: 'permitted-use', title: 'Permitted Use', icon: Globe },
    { id: 'user-accounts', title: 'User Accounts', icon: Users },
    { id: 'intellectual-property', title: 'Intellectual Property', icon: Shield },
    { id: 'proprietary-services', title: 'Proprietary Services', icon: Globe },
    { id: 'privacy', title: 'Privacy & Data Protection', icon: Shield },
    { id: 'third-party', title: 'Third-Party Services', icon: Globe },
    { id: 'disclaimers', title: 'Disclaimers', icon: AlertTriangle },
    { id: 'liability', title: 'Limitation of Liability', icon: AlertTriangle },
    { id: 'indemnification', title: 'Indemnification', icon: Shield },
    { id: 'termination', title: 'Termination', icon: AlertTriangle },
    { id: 'governing-law', title: 'Governing Law', icon: Globe },
    { id: 'general', title: 'General Provisions', icon: FileText },
    { id: 'contact', title: 'Contact Information', icon: Phone }
  ];

  const blogSections = [
    { id: 'blog-purpose', title: 'Purpose of Blog', icon: MessageSquare },
    { id: 'blog-ip', title: 'Intellectual Property', icon: Shield },
    { id: 'blog-comments', title: 'Comments & Submissions', icon: Users },
    { id: 'blog-guest', title: 'Guest Contributions', icon: Users },
    { id: 'blog-accuracy', title: 'Information Accuracy', icon: AlertTriangle },
    { id: 'blog-endorsements', title: 'No Endorsements', icon: Globe },
    { id: 'blog-changes', title: 'Content Changes', icon: FileText },
    { id: 'blog-reporting', title: 'Reporting Issues', icon: AlertTriangle },
    { id: 'blog-law', title: 'Governing Law', icon: Globe },
    { id: 'blog-contact', title: 'Contact Info', icon: Phone }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 overflow-y-auto  custom-scrollbar2">
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1 rounded-sm text-sm">
                Smac-X
              </div>
              <ChevronRight className="mx-2 text-gray-400" size={16} />
              <span className="text-gray-600 text-sm font-medium">Terms of Use</span>
            </div>
             <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm rounded flex items-center gap-1">
               <ArrowLeft strokeWidth={1} size={18}/> Go Back
                </button>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setActiveSection('main')}
                className={`px-4 py-2 rounded-sm font-medium transition-all ${
                  activeSection === 'main' 
                    ? 'bg-blue-100 text-blue-700 text-sm rounded-sm' 
                    : 'text-gray-600 hover:bg-gray-100 text-sm rounded-sm'
                }`}
              >
                Main Terms
              </button>
              {/* <button
                onClick={() => setActiveSection('blog')}
                className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                  activeSection === 'blog' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Blog Terms
              </button> */}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:w-80 lg:sticky lg:top-24 h-fit">
            <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-sm text-gray-900 mb-4 flex items-center">
                <FileText className="mr-2" size={20} />
                Table of Contents
              </h3>
              <nav className="space-y-1">
                {(activeSection === 'main' ? mainSections : blogSections).map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center text-sm text-gray-600"
                    >
                      <Icon className="mr-3 flex-shrink-0" size={16} />
                      {section.title}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Contact Card */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-sm shadow-sm text-white p-6 mt-6">
              <h3 className="font-semibold mb-3 flex items-center">
                <Phone className="mr-2" size={18} />
                Need Help?
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <Mail className="mr-2 flex-shrink-0" size={14} />
                  <span>info@smac-x.com</span>
                </div>
                <div className="flex items-center">
                  <Phone className="mr-2 flex-shrink-0" size={14} />
                  <span>+91-7207858989</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-2 flex-shrink-0" size={14} />
                  <span>Hyderabad, Telangana</span>
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1">
            {activeSection === 'main' ? (
              <div className="bg-white rounded-sm shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-t-sm">
                  <div className="flex items-center mb-1 ">
                    <FileText className="mr-3" size={32} />
                    <h1 className="text-md font-bold">Terms of Use</h1>
                  </div>
                  <p className="text-blue-300 text-md">
                    Welcome to Smac-X Inno Labs Private Limited. Please read these terms carefully before using our website.
                  </p>
                  {/* <div className="flex items-center mt-4 text-blue-200">
                    <Clock className="mr-2" size={16} />
                    <span className="text-sm">Last updated: January 2025</span>
                  </div> */}
                </div>

                <div className="p-8 space-y-8">
                  {/* Introduction */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                    <p className="text-gray-700 leading-relaxed">
                      By accessing or using our website at <strong>www.smac-x.com</strong>, you accept and agree to be bound by these Terms of Use. 
                      If you do not agree to these terms, you must not access or use our website.
                    </p>
                  </div>

                  {/* Section 1 */}
                  <section id="acceptance" className="scroll-mt-24">
                    <div className="flex items-center mb-2">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <Shield className="text-blue-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">1. Acceptance of Terms</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <p>
                        By using this Website, you affirm that you are of legal age and have full authority to enter into these Terms 
                        on behalf of yourself or any organization you represent. These Terms constitute a legally binding agreement 
                        between you and Smac-X regarding your use of the Website and associated services.
                      </p>
                    </div>
                  </section>

                  {/* Section 2 */}
                  <section id="modification" className="scroll-mt-24">
                    <div className="flex items-center mb-2">
                      <div className="bg-orange-100 p-2 rounded-lg mr-3">
                        <FileText className="text-orange-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900 ">2. Modification of Terms</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <p>
                        Smac-X reserves the right to update or revise these Terms at any time without prior notice. Modifications 
                        will be effective immediately upon being posted on this page, with the "Last Updated" date indicating the 
                        revision date. Continued use of the Website after such changes constitutes your acceptance of the updated Terms.
                      </p>
                    </div>
                  </section>

                  {/* Section 3 */}
                  <section id="permitted-use" className="scroll-mt-24">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-100 p-2 rounded-lg mr-3">
                        <Globe className="text-green-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">3. Permitted Use</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <p>
                        You are granted a limited, non-exclusive, non-transferable right to access and use the Website for 
                        informational, non-commercial purposes related to evaluating our services.
                      </p>
                      <div className="bg-red-50 border border-red-200 rounded-sm p-4 mt-4">
                        <h4 className="font-semibold text-red-800 mb-2">You agree not to:</h4>
                        <ul className="text-red-700 space-y-1">
                          <li>Use the Website for any unlawful purpose or activity that violates applicable laws or regulations</li>
                          <li>Interfere with the proper working of the Website or attempt unauthorized access to servers, systems, or databases</li>
                          <li>Copy, distribute, or commercially exploit Website content without express written permission from Smac-X</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Section 4 */}
                  <section id="user-accounts" className="scroll-mt-24">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3">
                        <Users className="text-purple-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">4. User Accounts and Authentication</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <p>Some areas of the Website may require user registration or account creation. By registering, you agree to:</p>
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-purple-50 p-4 rounded-sm">
                          <h4 className="font-semibold text-purple-800 mb-2">Your Responsibilities:</h4>
                          <ul className="text-purple-700 text-sm space-y-1">
                            <li>Provide accurate, complete, and current information</li>
                            <li>Maintain the confidentiality of your credentials</li>
                            <li>Notify us immediately of any unauthorized use</li>
                          </ul>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-semibold text-gray-800 mb-2">Important Note:</h4>
                          <p className="text-gray-700 text-sm">
                            Smac-X is not liable for any damages resulting from your failure 
                            to secure your login credentials.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Section 5 */}
                  <section id="intellectual-property" className="scroll-mt-24">
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                        <Shield className="text-indigo-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">5. Intellectual Property</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-6">
                        <p className="font-medium text-indigo-900 mb-3">
                          All intellectual property on this Website is the sole property of Smac-X or its licensors:
                        </p>
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <h5 className="font-semibold text-indigo-800">Content</h5>
                            <p className="text-indigo-700">Designs, logos, text, graphics</p>
                          </div>
                          <div>
                            <h5 className="font-semibold text-indigo-800">Media</h5>
                            <p className="text-indigo-700">Icons, images, audio-visual elements</p>
                          </div>
                          <div>
                            <h5 className="font-semibold text-indigo-800">Code</h5>
                            <p className="text-indigo-700">Source code, downloadable files</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                        <p className="text-red-800 font-medium">
                          ⚠️ Unauthorized use of any materials may violate legal statutes and result in civil or criminal penalties.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Section 6 */}
                  <section id="proprietary-services" className="scroll-mt-24">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-100 p-2 rounded-lg mr-3">
                        <Globe className="text-teal-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">6. Proprietary Services and Solutions</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <p>
                        The services and solutions described on the Website—including SAP consulting, web and mobile application 
                        development, and AI-based offerings—are proprietary to Smac-X and offered under distinct client engagements.
                      </p>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                        <p className="text-yellow-800">
                          <strong>Important:</strong> Details, scope, or capabilities shared on the Website are indicative and do not 
                          constitute a binding offer or guarantee of delivery unless formalized through a written service agreement.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Continue with remaining sections... */}
                  {/* For brevity, I'll add a few more key sections */}

                  {/* Section 9 */}
                  <section id="disclaimers" className="scroll-mt-24">
                    <div className="flex items-center mb-4">
                      <div className="bg-red-100 p-2 rounded-lg mr-3">
                        <AlertTriangle className="text-red-600" size={20} />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">7. Disclaimers of Warranty</h2>
                    </div>
                    <div className="prose prose-gray max-w-none">
                      <div className="bg-red-50 border border-red-200 rounded-sm p-6">
                        <p className="font-semibold text-red-900 mb-3">
                          The Website and its content are provided "as is" and "as available" without warranties of any kind.
                        </p>
                        <p className="text-red-800">
                          Smac-X does not warrant that the Website will be available uninterrupted, secure, or error-free, 
                          or that the content is accurate, complete, or reliable.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Contact Section */}
                  <section id="contact" className="scroll-mt-24">
                    <div className="flex items-center mb-6">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <Phone className="text-blue-600" size={20} />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">8. Contact Information</h2>
                    </div>
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-sm p-8">
                      <h3 className="text-xl font-semibold mb-4">
                        For any questions or concerns regarding these Terms, please contact:
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold mb-2">Smac-X Inno Labs Private Limited</h4>
                          <div className="space-y-2 text-blue-100">
                            <div className="flex items-center">
                              <Mail className="mr-2" size={16} />
                              <span>info@smac-x.com</span>
                            </div>
                            <div className="flex items-center">
                              <Phone className="mr-2" size={16} />
                              <span>+91-7207858989</span>
                            </div>
                            <div className="flex items-center">
                              <MapPin className="mr-2" size={16} />
                              <span>Hyderabad, Telangana, India</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white/10 rounded-lg p-4">
                          <h4 className="font-semibold mb-2">Business Hours</h4>
                          <p className="text-blue-100 text-sm">
                            Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                            Saturday: 10:00 AM - 2:00 PM IST<br />
                            Sunday: Closed
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-sm shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-t-sm">
                  <div className="flex items-center mb-2">
                    <MessageSquare className="mr-3" size={32} />
                    <h1 className="text-md font-bold">Blog Terms of Use</h1>
                  </div>
                  <p className="text-purple-300 text-md">
                    These terms govern your access to and use of our blog section at www.smac-x.com
                  </p>
                </div>

                <div className="p-8 space-y-12">
                  {/* Blog Introduction */}
                  <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                    <p className="text-gray-700 leading-relaxed">
                      By reading, sharing, or interacting with our blog, you agree to these Blog Terms, 
                      in addition to our general Terms of Use and Privacy Policy.
                    </p>
                  </div>

                  {/* Blog Purpose */}
                  <section id="blog-purpose" className="scroll-mt-24">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3">
                        <MessageSquare className="text-purple-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">1. Purpose of the Blog</h2>
                    </div>
                    <div className="prose prose-gray max-w-none text-sm">
                      <p>
                        The blog section is provided for informational and educational purposes only. It reflects the insights, 
                        opinions, and professional perspectives of our team, contributors, or guest authors.
                      </p>
                      <div className="grid md:grid-cols-4 gap-4 mt-4">
                        <div className="bg-blue-50 p-4 rounded-sm text-center">
                          <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Globe className="text-blue-600" size={20} />
                          </div>
                          <h4 className="font-semibold text-blue-900 text-sm">SAP</h4>
                        </div>
                        <div className="bg-green-50 p-4 rounded-sm text-center">
                          <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Globe className="text-green-600" size={20} />
                          </div>
                          <h4 className="font-semibold text-green-900 text-sm">Development</h4>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-sm text-center">
                          <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Globe className="text-purple-600" size={20} />
                          </div>
                          <h4 className="font-semibold text-purple-900 text-sm">AI</h4>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-sm text-center">
                          <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Globe className="text-orange-600" size={20} />
                          </div>
                          <h4 className="font-semibold text-orange-900 text-sm">Digital Transform</h4>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Add more blog sections as needed */}
                  <section id="blog-contact" className="scroll-mt-24">
                    <div className="flex items-center mb-6">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3">
                        <Phone className="text-purple-600" size={20} />
                      </div>
                      <h2 className="text-md font-bold text-gray-900">10. Contact Information</h2>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-sm p-8">
                      <h3 className="text-xl font-semibold mb-4">
                        For blog-related inquiries, content permissions, or guest contributions:
                      </h3>
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center">
                          <Mail className="mr-2" size={16} />
                          <span>info@smac-x.com</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="mr-2" size={16} />
                          <span>+91-7207858989</span>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
}

export default TermsOfUse;